import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, ArrowLeft, CheckCircle, Camera, Sun, Cpu } from 'lucide-react';
import QuoteForm from '../components/QuoteForm';

const TechVarifocalPage = () => {
  // Images officielles Ajax (utilisées avec autorisation)
  const media = {
    varifocalLens: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fs_cameras_varifocal_lens_9f5269ca4c%401.png",
    comparison4mm: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fmp_4_790c4b3e67%402.jpg",
    comparison8mm: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fmp_8_720c60cc0e%402.jpg",
    ajaxCamera: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fajax_camera_eddbda03c2%401.jpg",
    thirdParty: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fthird_party_camera_53aeae8001%401.jpg",
  };

  return (
    <div className="min-h-screen py-8 md:py-12" data-testid="tech-varifocal-page">
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 mb-8">
        <Link to="/superior-video" className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span>Retour à la vidéosurveillance</span>
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 border border-blue-500/40 rounded-full mb-4">
              <Eye className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-400 font-semibold">Technologie Superior</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-4">
              Objectif Varifocal <span className="text-blue-400">P-Iris</span>
            </h1>
            <p className="text-lg text-white/50 mb-6 leading-relaxed">
              L'objectif varifocal avec diaphragme P-Iris permet d'ajuster précisément l'angle de vue 
              et l'exposition pour capturer chaque détail critique, quelle que soit la luminosité.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#devis" className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-500/90 text-black font-semibold rounded-lg transition-colors">
                Demander un devis
              </a>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden bg-black aspect-video">
            <video 
              autoPlay 
              loop 
              muted 
              playsInline
              preload="auto"
              className="w-full h-full object-contain"
              poster={media.varifocalLens}
            >
              <source src="/videos/varifocal-lens.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      {/* Section: Scène nette, contexte visible */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 md:p-10">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded">NOUVEAU</span>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-4">
            Scène nette, contexte visible
          </h2>
          <p className="text-white/50 mb-8 max-w-3xl leading-relaxed">
            Les caméras Superior sont conçues pour capturer chaque événement critique dans l'image, 
            ne laissant aucune place aux angles morts ni aux détails manqués. L'objectif varifocal 
            avec autofocus peut effectuer un zoom avant sur les visages, les panneaux ou les plaques 
            d'immatriculation, ou un zoom arrière pour englober l'ensemble de la scène.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              { title: "Focale réglable", desc: "De 2,8 à 12 mm pour s'adapter à chaque situation" },
              { title: "Exposition équilibrée", desc: "Objectif P-Iris motorisé pour une image parfaite" },
              { title: "Autofocus temps réel", desc: "Images toujours nettes, même en mouvement" },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                  <p className="text-sm text-white/50">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Comparaison 4Mp vs 8Mp */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-xl overflow-hidden">
              <img src={media.comparison4mm} alt="Vue 4 Mp" className="w-full h-64 object-cover" />
              <div className="bg-black/40 p-4">
                <h4 className="text-white font-semibold">Résolution 4 Mp</h4>
                <p className="text-sm text-white/50">Idéal pour les vues d'ensemble</p>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden">
              <img src={media.comparison8mm} alt="Vue 8 Mp" className="w-full h-64 object-cover" />
              <div className="bg-black/40 p-4">
                <h4 className="text-white font-semibold">Résolution 8 Mp</h4>
                <p className="text-sm text-white/50">Détails fins et zoom numérique</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: P-Iris vs caméra standard */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-4">
            Aucun détail manqué à cause de la lumière
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto">
            L'objectif P-Iris motorisé ajuste en continu l'ouverture, équilibrant l'exposition 
            et la profondeur de champ en temps réel.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-xl overflow-hidden border border-blue-500/40">
            <div className="bg-black h-72">
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                preload="auto"
                className="w-full h-full object-cover"
              >
                <source src="/videos/ajax-camera-comparison.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="bg-gradient-to-r from-blue-900/50 to-fgs-surface p-6">
              <h4 className="text-white font-bold text-lg mb-2">Caméra Ajax Superior</h4>
              <p className="text-white/50 text-sm">
                Image parfaitement exposée grâce à l'objectif P-Iris. Aucune zone surexposée 
                ni ombre qui cache les détails.
              </p>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden border border-white/5">
            <div className="bg-black h-72">
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                preload="auto"
                className="w-full h-full object-cover"
              >
                <source src="/videos/third-party-camera.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="bg-white/[0.02] p-6">
              <h4 className="text-white font-bold text-lg mb-2">Caméra tierce</h4>
              <p className="text-white/50 text-sm">
                Zones surexposées près des fenêtres et ombres qui masquent les détails importants.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="bg-gradient-to-br from-blue-900/30 to-fgs-surface border border-blue-500/20 rounded-2xl p-6 md:p-10">
          <h2 className="text-2xl font-semibold text-white uppercase mb-8 text-center">
            Pourquoi choisir l'objectif P-Iris ?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Eye, title: "Zoom optique 4×", desc: "De 2,8mm (grand angle) à 12mm (téléobjectif)" },
              { icon: Sun, title: "True WDR 130dB", desc: "Plage dynamique étendue pour les contre-jours" },
              { icon: Camera, title: "Autofocus rapide", desc: "Mise au point automatique en temps réel" },
              { icon: Cpu, title: "Contrôle P-Iris", desc: "Ouverture motorisée pour une exposition parfaite" },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-blue-400" />
                </div>
                <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                <p className="text-sm text-white/50">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulaire de devis */}
      <section id="devis" className="max-w-[1400px] mx-auto px-4 md:px-12 scroll-mt-24">
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 md:p-10">
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                Intéressé par les caméras P-Iris ?
              </h2>
              <p className="text-white/50 mb-6">
                Nos experts vous conseillent sur le choix des caméras Superior adaptées à votre projet.
              </p>
              <div className="space-y-3">
                {[
                  "Caméras BulletCam, DomeCam et TurretCam disponibles",
                  "Résolutions 4 Mp et 8 Mp",
                  "Installation par techniciens certifiés Ajax",
                  "Garantie 4 ans FGS incluse",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-white text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <QuoteForm category="video" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default TechVarifocalPage;
